import { Stripe, StripeElements } from '@stripe/stripe-js';
import { CardNumberElement } from '@stripe/react-stripe-js';

import {
  IFirestoreOfferPurchaseCreateRequest,
  IFirestoreOfferPurchases,
  IFirestoreOfferPurchaseUpdateRequest,
} from '@/src/modules/firestore/payment/payment.interface';
import { callFirebaseFunction } from '@/src/modules/firestore/firestore.service';

export const createPaymentIntent = async (
  offerId: string,
  promoId?: string,
  redeemCode?: string
) => {
  const payload: IFirestoreOfferPurchaseCreateRequest = {
    offer: offerId,
    isWeb: true,
  };
  if (promoId) {
    payload.promo = promoId;
  }
  if (redeemCode) {
    payload.promoRedeemCode = redeemCode;
  }
  return callFirebaseFunction<
    IFirestoreOfferPurchaseCreateRequest,
    IFirestoreOfferPurchases
  >('payment', 'offerPurchase', 'create', payload);
};

export const updatePaymentIntent = async (
  offerId: string,
  paymentId: string,
  redeemCode: string,
  revision: string
) => {
  return callFirebaseFunction<
    IFirestoreOfferPurchaseUpdateRequest,
    IFirestoreOfferPurchases
  >('payment', 'offerPurchase', 'update', {
    offer: offerId,
    id: paymentId,
    promoRedeemCode: redeemCode,
    revision: revision,
  });
};

export const processPaymentProvider = async (
  stripe: Stripe | null,
  elements: StripeElements | null,
  payment: IFirestoreOfferPurchases,
  locale: string
) => {
  if (!stripe || !elements) {
    return;
  }
  const { error: submitError } = await elements.submit();
  if (submitError) {
    console.log(submitError);
    throw new Error(submitError.message);
  }

  const result = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: `${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/payment/success`,
    },
    clientSecret: payment.stripePaymentIntentClientSecret,
  });

  if (result.error) {
    throw new Error(result.error.message);
  }

  return true;
};

export const processPayment = async (
  stripe: Stripe | null,
  elements: StripeElements | null,
  payment: IFirestoreOfferPurchases,
  locale: string,
  clickId: string
) => {
  if (!stripe || !elements) {
    return;
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    console.log('Unexpected submit error');
    throw new Error(submitError.message);
  }

  const cardNumber = elements.getElement(CardNumberElement);
  if (cardNumber) {
    const { paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumber,
      metadata: {
        impact_click_id: clickId,
      },
    });

    if (paymentMethod) {
      const result = await stripe.confirmCardPayment(
        payment.stripePaymentIntentClientSecret,
        {
          return_url: `${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/payment/success`,
          payment_method: paymentMethod.id,
        }
      );
      if (result.error) {
        throw new Error(result.error.message);
      }

      return result.paymentIntent;
    }
  } else {
    throw new Error('Unexpected error on card');
  }
};
